
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { RestaurantSelectorBaseProps } from './types';

interface RestaurantCardSelectorProps extends RestaurantSelectorBaseProps {
  onMultiSelection: (restaurantId: string, closeAfterSelection?: boolean) => void;
  showMetrics?: boolean;
  cardClassName?: string;
}

export function RestaurantCardSelector({
  restaurants,
  selectedRestaurants,
  onRestaurantChange,
  onMultiSelection,
  showMetrics = false,
  cardClassName = '',
  className
}: RestaurantCardSelectorProps) {
  return (
    <div className={cn("grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4", className)}>
      <Card 
        key="all"
        className={cn(
          "cursor-pointer transition-colors hover:bg-accent hover:text-accent-foreground",
          selectedRestaurants.includes('all') ? 'border-2 border-primary' : '',
          cardClassName
        )}
        onClick={() => onRestaurantChange(['all'])}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback>
                ALL
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">All Restaurants</div>
              <div className="text-xs text-muted-foreground">View consolidated data</div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {restaurants.map(restaurant => (
        <Card 
          key={restaurant.id}
          className={cn(
            "cursor-pointer transition-colors hover:bg-accent hover:text-accent-foreground",
            selectedRestaurants.includes(restaurant.id) ? 'border-2 border-primary' : '',
            cardClassName
          )}
          onClick={() => onMultiSelection(restaurant.id)}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                {restaurant.logoUrl ? (
                  <AvatarImage src={restaurant.logoUrl} alt={restaurant.name} />
                ) : null}
                <AvatarFallback>
                  {restaurant.name.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="font-medium">{restaurant.name}</div>
                {restaurant.description && (
                  <div className="text-xs text-muted-foreground">{restaurant.description}</div>
                )}
              </div>
            </div>
            
            {showMetrics && restaurant.metrics && (
              <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t text-sm">
                {Object.entries(restaurant.metrics).map(([key, value]) => (
                  <div key={key}>
                    <div className="text-xs text-muted-foreground">{key}</div>
                    <div className="font-medium">{value}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
